import { toast } from "sonner";
import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import {
  FeedbackWithAuditDto,
  PublicOrganizationFeedbackThemeSummary,
} from "@/types";
import {
  createFeedback,
  fetchFeedbacksByAccountId,
  fetchFeedbackById,
  updateFeedback,
  UpdateFeedbackRequest,
  fetchAllFeedbacks,
  deleteFeedback,
  fetchFeedbackRelatedObjects,
  fetchOrganizationFeedbackThemes,
} from "@/services/frontendServices/feedbacksServices";
import { RelatedObjectType } from "@/enums";
import { FeedbackFormData } from "@/components/forms/create-feedback-form";
import { accountKeys } from "./useAccounts";
import { getErrorMessage, showErrorToast } from "@/lib/error-handling";

// Keys for queries, to maintain consistency
export const feedbackKeys = {
  all: ["feedbacks"] as const,
  lists: () => [...feedbackKeys.all, "list"] as const,
  list: (accountId: string) => [...feedbackKeys.lists(), accountId] as const,
  details: () => [...feedbackKeys.all, "detail"] as const,
  detail: (id: string) => [...feedbackKeys.details(), id] as const,
  global: () => [...feedbackKeys.all, "global"] as const,
  organizationThemes: () =>
    [...feedbackKeys.all, "organizationThemes"] as const,
};

export const useFeedbacksByAccount = (accountId: string) => {
  return useQuery<FeedbackWithAuditDto[]>({
    queryKey: feedbackKeys.list(accountId),
    queryFn: () => fetchFeedbacksByAccountId(accountId),
    enabled: !!accountId,
  });
};

export const useCreateFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation<
    FeedbackWithAuditDto,
    Error,
    FeedbackFormData & { accountId: string }
  >({
    mutationFn: ({ accountId, ...formData }) =>
      createFeedback(formData, accountId),
    onSuccess: (newFeedback, variables) => {
      // Optimistically update the cache
      queryClient.setQueryData<FeedbackWithAuditDto[]>(
        feedbackKeys.list(variables.accountId),
        (oldData = []) => {
          return [newFeedback, ...oldData];
        }
      );

      // Keep the organization feedback table responsive when it is already loaded
      queryClient.setQueryData<FeedbackWithAuditDto[]>(
        feedbackKeys.global(),
        (oldData) => {
          if (!oldData) return oldData;
          return [newFeedback, ...oldData];
        }
      );

      // Invalidates the account's feedbacks list query to force a refetch
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.list(variables.accountId),
      });

      // Invalidate organization-scoped feedback data used by the global feedback page
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.global(),
      });

      queryClient.invalidateQueries({
        queryKey: feedbackKeys.organizationThemes(),
      });

      // Invalidate account cache to update last updated date
      queryClient.invalidateQueries({
        queryKey: accountKeys.detail(variables.accountId),
      });

      toast.success("Feedback saved successfully!", {
        description: `Feedback has been created.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error creating feedback",
        defaultMessage: "Failed to create feedback",
        duration: 5000,
      });
    },
  });
};

export const useFeedback = (fbId: string) => {
  return useQuery({
    queryKey: feedbackKeys.detail(fbId),
    queryFn: () => fetchFeedbackById(fbId),
    enabled: !!fbId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useUpdateFeedback = ({
  showSuccessToast = true,
}: { showSuccessToast?: boolean } = {}) => {
  const queryClient = useQueryClient();

  return useMutation<
    FeedbackWithAuditDto,
    Error,
    { feedbackId: string; formData: UpdateFeedbackRequest }
  >({
    mutationFn: ({ feedbackId, formData }) =>
      updateFeedback(feedbackId, formData),
    onSuccess: (updatedFeedback, variables) => {
      const { feedbackId, formData } = variables;

      // Update the individual feedback detail
      queryClient.setQueryData<FeedbackWithAuditDto>(
        feedbackKeys.detail(feedbackId),
        updatedFeedback
      );

      // Update the specific account's feedback list
      const accountId = updatedFeedback.account?.id;
      if (accountId) {
        queryClient.setQueryData<FeedbackWithAuditDto[]>(
          feedbackKeys.list(accountId),
          (oldData) => {
            if (!oldData) return oldData;
            return oldData.map((feedback) =>
              feedback.id === updatedFeedback.id ? updatedFeedback : feedback
            );
          }
        );
      }

      // Update the organization-wide feedback cache when present
      queryClient.setQueryData<FeedbackWithAuditDto[]>(
        feedbackKeys.global(),
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((feedback) =>
            feedback.id === updatedFeedback.id ? updatedFeedback : feedback
          );
        }
      );

      // Invalidate related queries to force a refetch
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.detail(feedbackId),
      });

      // Invalidate related objects queries to force a refetch
      queryClient.invalidateQueries({
        queryKey: ["feedbackRelatedObjects", feedbackId],
      });

      // Invalidate account cache to update last updated date
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });
      }

      queryClient.invalidateQueries({
        queryKey: feedbackKeys.global(),
      });

      queryClient.invalidateQueries({
        queryKey: feedbackKeys.organizationThemes(),
      });

      if (!showSuccessToast) return;

      // Show specific messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archived" : "unarchived";
        toast.success(`Feedback ${action} successfully!`, {
          description: `Feedback has been ${action}.`,
          duration: 3000,
        });
      } else {
        toast.success("Feedback updated successfully!", {
          description: `Feedback has been updated.`,
          duration: 3000,
        });
      }
    },
    onError: (error, variables) => {
      const errorMessage = getErrorMessage(error, "Failed to update feedback");
      const { formData } = variables;

      // Show specific error messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archive" : "unarchive";
        toast.error(`Failed to ${action} feedback`, {
          description: errorMessage,
          duration: 5000,
        });
      } else {
        toast.error("Error updating feedback", {
          description: errorMessage,
          duration: 5000,
        });
      }
    },
  });
};

/**
 * Hook to delete an existing feedback.
 * Invalidates all related caches including account data.
 */
export const useDeleteFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { feedbackId: string }>({
    mutationFn: async ({ feedbackId }) => {
      await deleteFeedback(feedbackId);
    },
    onSuccess: (_, { feedbackId }) => {
      // Remove the feedback from the detail cache
      queryClient.removeQueries({
        queryKey: feedbackKeys.detail(feedbackId),
      });

      // Invalidate all feedback lists (this covers all account lists)
      queryClient.invalidateQueries({
        queryKey: feedbackKeys.all,
      });

      // Invalidate all accounts to update last updated dates
      queryClient.invalidateQueries({
        queryKey: accountKeys.all,
      });

      toast.success("Feedback deleted successfully!", {
        description: "The feedback has been deleted.",
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error deleting feedback",
        defaultMessage: "Failed to delete feedback",
        duration: 5000,
      });
    },
  });
};

// Hook to get all feedbacks across all accounts for global dashboard statistics
export const useAllFeedbacks = () => {
  return useQuery<FeedbackWithAuditDto[], Error>({
    queryKey: feedbackKeys.global(),
    queryFn: () => fetchAllFeedbacks(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useOrganizationFeedbackThemes = (period?: {
  from: string;
  until: string;
}) => {
  return useQuery<PublicOrganizationFeedbackThemeSummary | null, Error>({
    // Under the organizationThemes() prefix, so its invalidations still cover every period.
    queryKey: [
      ...feedbackKeys.organizationThemes(),
      period?.from ?? "all",
      period?.until ?? "all",
    ],
    queryFn: () => fetchOrganizationFeedbackThemes(period),
    // Keep the last period's bubbles on screen while a new period loads.
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

interface UseFeedbackRelatedObjectsParams {
  feedbackId?: string;
  types: RelatedObjectType[];
}

export function useFeedbackRelatedObjects({
  feedbackId,
  types,
}: UseFeedbackRelatedObjectsParams) {
  return useQuery({
    queryKey: ["feedbackRelatedObjects", feedbackId, types],
    queryFn: async () => {
      const res = await fetchFeedbackRelatedObjects(feedbackId!, types);

      // If response contains an array of items
      if (Array.isArray(res)) {
        const grouped: Record<string, unknown[]> = {};
        const key =
          types.length === 1 && types[0] === RelatedObjectType.ACTIVITY
            ? "activities"
            : "items";
        grouped[key] = res;
        return grouped;
      }

      return res;
    },
    enabled: !!feedbackId && !!types.length,
  });
}
